import type { TPlayerDiagnosticsSnapshot } from '@lucimark/shared/models/player-diagnostics'
/**
 * Audience metrics on `IPlaybackHeartbeatBody.audienceAggregate` (v1).
 * @see ./audience-heartbeat-aggregate-semantics.md — versioning, rollups, and CV vs motion semantics.
 */
import type { TPlayerBrightnessControl, TPlayerBrightnessMethod, TPlayerBrightnessMode } from '@lucimark/shared/models/player-brightness'
import type { TPlayerPowerSource } from '@lucimark/shared/models/player-power'
import type { TScreenOrientation, TScreenRotation } from '@lucimark/shared/models/screen-rotation'
import { playerDiagnosticsSnapshotSchema } from '@lucimark/shared/models/player-diagnostics'
import { playerBrightnessHeartbeatFieldsSchema } from '@lucimark/shared/models/player-brightness'
import { playerPowerHeartbeatFieldsSchema } from '@lucimark/shared/models/player-power'
import { screenOrientationSchema, screenRotationSchema } from '@lucimark/shared/models/screen-rotation'
import z from 'zod'

/** Only `1` is valid today; bump when breaking aggregate shape or field meaning. */
export const AUDIENCE_HEARTBEAT_AGGREGATE_VERSION = 1 as const

/** One asset play: URN and whole seconds it was visible since the last heartbeat. */
export interface IAssetPlay {
  assetUrn: string
  /** Whole seconds this asset was visible since the last heartbeat. */
  durationSeconds: number
  /**
   * Spots / presentations started in the flush window (P0″).
   * Omitted on legacy players ⇒ 1 at ingest. Explicit `0` = mid-spot cadence continuation
   * (duration only; spot already counted earlier while still on-air).
   */
  playCount?: number
  /**
   * When this play occurred (ISO-8601 or epoch ms). Ingest guards prefer this for wall date/bucket
   * when plausible; otherwise server arrival time is used.
   */
  playedAt?: string | number
}

/**
 * Hardware capacity snapshot from the player (e.g. tauri-plugin-device-capacity).
 * Persisted on the player row for the app players view.
 */
export interface IHardwareCapacitySnapshot {
  /** Derived rendering budget tier: low | mid | high. */
  tier: string
  /** Total device RAM in MB. */
  totalRamMb: number
  /** Currently available RAM in MB. */
  availRamMb: number
  /** Number of logical CPU cores. */
  cpuCores: number
  /** Max CPU frequency (core 0) in MHz; -1 when unreadable. */
  maxCpuFreqMhz: number
  /** OpenGL ES version string, e.g. "3.2"; "desktop" on non-Android. */
  glEsVersion: string
  /** True when the OS is in a low-memory state. */
  isLowMemory: boolean
  /** Android API level (e.g. 33); 0 on desktop. */
  androidApiLevel: number
  /** Primary ABI, e.g. "arm64-v8a". */
  abi: string
}

/** One video input device reported by the player (`enumerateDevices`); shown in the management app camera dropdown. */
export interface IAudienceVideoInputDevice {
  deviceId: string
  /** Human-readable name; may match `deviceId` until the browser grants camera permission. */
  label: string
}

/**
 * v1 aggregate: interval the scores summarize; optional motion/CV fields from on-device pipelines.
 * `version` + `cvPersonModelId` (when present) anchor historical interpretation; see `audience-heartbeat-aggregate-semantics.md`.
 */
export interface IAudienceHeartbeatAggregate {
  version: typeof AUDIENCE_HEARTBEAT_AGGREGATE_VERSION
  /** Seconds covered by this sample (must match player heartbeat cadence in practice). */
  intervalSeconds: number
  motionScore?: number
  presenceScore?: number
  /**
   * Seconds in the interval with motion above the active threshold (sample-count × luma pipeline cadence), capped at `intervalSeconds`.
   * Motion-based proxy only — not verified person dwell time.
   */
  estimatedActiveDwellSeconds?: number
  /**
   * Count of motion “sessions” in the interval (bursts separated by debounced idle). Not unique humans; see product copy for #291.
   */
  motionBasedSessionCount?: number
  /**
   * On-device COCO-SSD **detector** id (audit trail). Does not encode IOU tracker tuning (that follows the player build).
   * Only sent when super-admin tenant flag `audiencePersonCvAnalytics` is on.
   */
  cvPersonModelId?: string
  /**
   * Qualified **detection tracks** that **started** in the interval (IOU tracker + min hits/span). **Not** unique visitors.
   */
  personQualifiedTrackCount?: number
  /**
   * **Sum** of qualified-track dwell in the interval (seconds per track capped at `intervalSeconds`). **Non-deduplicated**
   * across intervals — not “unique visitor minutes.”
   */
  personDwellSeconds?: number
}

/** Optional body of POST /players/:b64PlayerId/playback-heartbeat (uptime + proof-of-play). */
export interface IPlaybackHeartbeatBody {
  /** Optional storage used % (0–100) persisted on the player row for the app players view. */
  storageUsedPercent?: number
  /**
   * Per-asset proof-of-play: each entry is one asset that was visible for the given durationSeconds since the last heartbeat.
   * The player sends this when it has accumulated plays; the API writes one asset_playback_event row per entry.
   * Capped at {@link PLAYBACK_HEARTBEAT_MAX_ASSET_PLAYS} per request (see Zod schema and API route).
   * The whole JSON body is also capped at {@link PLAYBACK_HEARTBEAT_MAX_BODY_UTF8_BYTES} UTF-8 bytes at the API route.
   */
  assetPlays?: IAssetPlay[]
  /**
   * Global advertiser-portal `ad_order.id` for proof-of-play attribution when the screen is on a portal fulfillment list.
   * Must be a UUID when sent; invalid values are ignored at ingest.
   */
  orderId?: string
  /**
   * Network operator id at play time (from registration or heartbeat). Stored on attributed proof-of-play rows only.
   */
  networkOperatorId?: string
  /**
   * Playlist currently providing the content on screen, when applicable (stored as `playlist_id` on proof-of-play rows).
   * Same meaning as {@link playlistId}; if both are sent they must be identical.
   */
  playlistUrn?: string
  /** Preferred alias of {@link playlistUrn} for proof-of-play attribution (same URN string). */
  playlistId?: string
  /**
   * Geohash of the player’s reported position at this heartbeat (`GEOHASH_PLAYER_HEARTBEAT_PRECISION` in `@lucimark/shared/helpers/geohash`).
   * When proof-of-play `assetPlays` are sent, stored on each `asset_playback_event` row.
   */
  playerGeohash?: string
  /** Optional hardware capacity snapshot from the player; persisted on the player row for the app. */
  hardwareCapacity?: IHardwareCapacitySnapshot
  /** Optional app version (e.g. semver). Persisted on the player row; keeps app/list in sync when player uses polling. */
  version?: string
  /** Optional web bundle version (e.g. OTA git hash). Persisted on the player row; keeps app/list in sync when player uses polling. */
  webVersion?: string
  /** Last-reported display rotation (degrees); persisted on the player row for the app. */
  screenRotation?: TScreenRotation
  /** Optional orientation label from the player (horizontal / vertical). */
  screenOrientation?: TScreenOrientation
  /**
   * Inner viewport width in CSS pixels (`window.innerWidth`); must be sent with {@link screenHeightPx}.
   * Persisted on the player row for operators.
   */
  screenWidthPx?: number
  /**
   * Inner viewport height in CSS pixels (`window.innerHeight`); must be sent with {@link screenWidthPx}.
   */
  screenHeightPx?: number
  /** When true, the player’s “current status” panel (debug / CurrentState) is visible. */
  currentStatusOverlayVisible?: boolean
  /** When true, the player’s timers overlay is visible. */
  timersOverlayVisible?: boolean
  /**
   * Android: first 8 characters of `Settings.Secure.ANDROID_ID` (from native deviceId).
   * Persisted on the player row for dashboard ESN display; ignored on non-Android clients.
   */
  androidIdPrefix?: string
  /**
   * Android Device Owner (last sample). Persisted for Studio shell readiness / silent APK OTA.
   */
  deviceOwner?: boolean
  /**
   * Lucimark Accessibility service enabled (last sample). Persisted for Studio Remoto gating.
   */
  accessibilityEnabled?: boolean
  /** Android: where the device draws power from (last sample). Omitted when unavailable. */
  powerSource?: TPlayerPowerSource
  /** Android: false on battery-less devices (TV boxes). */
  batteryPresent?: boolean
  /** Android: battery level 0–100; omitted without a battery. */
  batteryPercent?: number
  /** Android: battery is charging or full. */
  batteryCharging?: boolean
  /** Android: current effective screen brightness, 0–100. */
  brightnessPercent?: number
  /** Android: automatic (ambient-light) or manual brightness. */
  brightnessMode?: TPlayerBrightnessMode
  /** Android: `unsupported` on HDMI boxes whose backlight Android cannot reach. */
  brightnessControl?: TPlayerBrightnessControl
  /** Android: how brightness is applied (`window` override vs `system` setting). */
  brightnessMethod?: TPlayerBrightnessMethod
  /** Android: the `WRITE_SETTINGS` app-op is granted (enables the `system` method). */
  writeSettingsGranted?: boolean
  /** Android: device has an ambient light sensor. */
  ambientLightSupported?: boolean
  /** Android: latest ambient light reading (lux), only while the sensor is sampled. */
  ambientLux?: number
  /**
   * On-device audience metrics (no video). Accepted only when tenant `audienceAnalytics` and player `audienceAnalyticsEnabled` are true.
   * Server persists at least one numeric metric (motion or presence); version-1 shape is strict. Gate logic is centralized in `audienceHeartbeatPolicy.ts`.
   */
  audienceAggregate?: IAudienceHeartbeatAggregate
  /**
   * Video input devices from `navigator.mediaDevices` (audience-facing cameras).
   * Forwarded when tenant allows audience webcam preview; persisted for the management app dropdown.
   */
  audienceVideoInputDevices?: IAudienceVideoInputDevice[]
  /**
   * Player-reported offset from UTC to local wall time: `-new Date().getTimezoneOffset()` (minutes).
   * Used for 15-minute proof-of-play rollups; omit when unknown so server leaves wall-clock fields null on events.
   */
  utcOffsetMinutes?: number
  /**
   * Sum of resolved segment durations (seconds) for one full materialized playback loop — same ordering as the
   * player’s active queue (`IQueueFrame.duration` per segment). Omit when idle or unknown; used as a coarse signal
   * of how much programmed content one loop carries.
   */
  segmentResolvedLoopSeconds?: number
  /**
   * When true, the player is **online** but outside configured weekly availability windows in **device local time**:
   * schedule playback is suppressed and the screen shows black (overlays unchanged). Lets the management app
   * distinguish policy blackout from offline or errors. Omit when unregistered; send `false` when registered and not in blackout.
   */
  weeklyAvailabilityPlaybackSuppressed?: boolean
  /**
   * When the timers overlay is on, whether the player suspects the playback timer / scheduler readout is stuck
   * (visible frame past its slot with flat timing). Send `false` when overlay is on and timing looks healthy.
   */
  playbackTimerStuckSuspected?: boolean
  /** Ms the visible frame has exceeded its scheduled slot when {@link playbackTimerStuckSuspected} is true. */
  playbackTimerStuckOverrunMs?: number
  /** Queue frame id visible when the stuck sample was taken. */
  playbackTimerStuckFrameId?: string
  /** Wall ms since the visible frame identity last changed (diagnostic). */
  playbackTimerStuckVisibleDwellMs?: number
  /** Ms since playback timing `frameElapsedMs` last changed (diagnostic). */
  playbackTimerStuckTimingFlatlineMs?: number
  /** Bounded on-device diagnostics snapshot (Phase 1 player self-diagnostics). */
  playerDiagnostics?: TPlayerDiagnosticsSnapshot
  /**
   * Process boot time (ISO 8601). Sent once per boot on early heartbeats for disruption-cause attribution.
   * @see {@link packages/shared/models/player-boot-telemetry.ts}
   */
  lastBootAt?: string
  /** When false, the previous session did not shut down cleanly (crash/kill). Omitted when unknown. */
  lastBootWasCleanShutdown?: boolean
  /** When true, the device reported a battery or power event at boot (Android when available). */
  lastBootHadBatteryOrPowerEvent?: boolean
  /** Android boot kind (`cold_boot` | `app_restart`). */
  lastBootKind?: 'cold_boot' | 'app_restart'
}

/** Last-reported playback timer stuck sample persisted on the player row (Tenant DO). */
export interface ILastReportedPlaybackTimerStuck {
  suspected: boolean
  overrunMs?: number | null
  frameId?: string | null
  visibleDwellMs?: number | null
  timingFlatlineMs?: number | null
}

/** Maps on-device stuck detection into optional playback-heartbeat fields. */
export function buildPlaybackTimerStuckHeartbeatFields(args: {
  /** Metadata only (e.g. {@link IPlaybackHeartbeatBody.timersOverlayVisible}); does not gate emission. */
  showTimersOverlay?: boolean
  status: {
    suspected: boolean
    overrunMs: number
    frameId: string | null
    visibleDwellMs: number
    timingFlatlineMs: number
  } | null
}): Pick<
  IPlaybackHeartbeatBody,
  | 'playbackTimerStuckSuspected'
  | 'playbackTimerStuckOverrunMs'
  | 'playbackTimerStuckFrameId'
  | 'playbackTimerStuckVisibleDwellMs'
  | 'playbackTimerStuckTimingFlatlineMs'
> {
  void args.showTimersOverlay
  if (args.status == null) {
    return {}
  }
  const out: Pick<
    IPlaybackHeartbeatBody,
    | 'playbackTimerStuckSuspected'
    | 'playbackTimerStuckOverrunMs'
    | 'playbackTimerStuckFrameId'
    | 'playbackTimerStuckVisibleDwellMs'
    | 'playbackTimerStuckTimingFlatlineMs'
  > = {
    playbackTimerStuckSuspected: args.status.suspected
  }
  if (args.status.suspected) {
    out.playbackTimerStuckOverrunMs = args.status.overrunMs
    if (args.status.frameId != null && args.status.frameId !== '') {
      out.playbackTimerStuckFrameId = args.status.frameId
    }
    out.playbackTimerStuckVisibleDwellMs = args.status.visibleDwellMs
    out.playbackTimerStuckTimingFlatlineMs = args.status.timingFlatlineMs
  }
  return out
}

/** Geohash string (base32, 1–12 chars) from the player at heartbeat time; stored on proof-of-play rows. */
export const playbackHeartbeatPlayerGeohashSchema = z
  .string()
  .min(1)
  .max(12)
  .regex(/^[0-9b-hjkmnp-z]{1,12}$/i)
  .transform(s => s.toLowerCase())

/**
 * Nominal playback-heartbeat / uptime coverage window (ms). Player cadence and API
 * `player_playback_event` coalesce use this value so covered intervals stay contiguous.
 */
export const PLAYBACK_HEARTBEAT_COVERAGE_MS = 60_000 as const

/**
 * Min gap between proof-of-play `asset_playback_event` flush windows (P2).
 * Matches {@link PLAYBACK_HEARTBEAT_COVERAGE_MS} so player cadence and TenantDO ingest
 * cannot drift. Stale players that still POST per-spot plays are merged inside this window.
 */
export const PROOF_OF_PLAY_FLUSH_MIN_GAP_MS = PLAYBACK_HEARTBEAT_COVERAGE_MS

/**
 * Maximum `assetPlays` entries per playback-heartbeat body (limits DO batch insert / SQL size).
 * Keep in sync with API route checks before forwarding to the Tenant DO.
 */
export const PLAYBACK_HEARTBEAT_MAX_ASSET_PLAYS = 500 as const

/**
 * Maximum UTF-8 size (octets) of the POST `/playback-heartbeat` JSON body.
 * Limits memory/CPU for `JSON.parse` / Zod when `assetUrn` or other strings are pathologically long.
 * Enforced in the API route (Content-Length hint + measured size after read). Keep in sync with that route.
 */
/** 2 MiB */
export const PLAYBACK_HEARTBEAT_MAX_BODY_UTF8_BYTES = 2_097_152 as const

/**
 * Default budget for POST `/playback-heartbeat` per `(tenantId, playerId)`.
 * Enforced fleet-wide via Cloudflare Rate Limiting binding `PLAYBACK_HEARTBEAT_RATE_LIMITER`
 * (wrangler limit/period must match); falls back to isolate-local `rateLimitAllow` in tests.
 * Generous so legitimate ~1 Hz heartbeats rarely 429.
 */
export const PLAYBACK_HEARTBEAT_ROUTE_MAX_PER_MINUTE = 300 as const

/** Zod schema for one asset play in the heartbeat body. */
export const assetPlaySchema = z.object({
  assetUrn: z.string(),
  /** Whole seconds this asset was visible since the last heartbeat. */
  durationSeconds: z.number().int().positive(),
  /**
   * Spots / presentations started for this asset in the flush window (P0″).
   * Optional for older players — omitted ⇒ 1 at ingest/rollup.
   * `0` allowed for mid-spot cadence continuation (duration only; spot already counted).
   */
  playCount: z.number().int().nonnegative().optional(),
  /**
   * Client play time (ISO-8601 string or epoch milliseconds). Optional — older players omit.
   * When ingest guards are on, used for wall date/bucket if within the plausible window.
   */
  playedAt: z.union([z.string().min(1).max(64), z.number().finite()]).optional()
})

/** Zod schema for hardware capacity snapshot in playback-heartbeat. */
export const hardwareCapacitySnapshotSchema = z.object({
  tier: z.string(),
  totalRamMb: z.number(),
  availRamMb: z.number(),
  cpuCores: z.number(),
  maxCpuFreqMhz: z.number(),
  glEsVersion: z.string(),
  isLowMemory: z.boolean(),
  androidApiLevel: z.number(),
  abi: z.string()
})

/** Strict v1 audience aggregate (heartbeat JSON). */
export const audienceHeartbeatAggregateSchema = z.object({
  version: z.literal(AUDIENCE_HEARTBEAT_AGGREGATE_VERSION),
  intervalSeconds: z.number().int().positive().max(600),
  motionScore: z.number().min(0).max(1).optional(),
  presenceScore: z.number().min(0).max(1).optional(),
  estimatedActiveDwellSeconds: z.number().min(0).max(600).optional(),
  motionBasedSessionCount: z.number().int().min(0).max(10_000).optional(),
  cvPersonModelId: z.string().min(1).max(64).optional(),
  personQualifiedTrackCount: z.number().int().min(0).max(10_000).optional(),
  personDwellSeconds: z.number().min(0).max(600).optional()
}).strict()

export type TAudienceHeartbeatAggregate = z.infer<typeof audienceHeartbeatAggregateSchema>

export const audienceVideoInputDeviceEntrySchema = z.object({
  deviceId: z.string().min(1).max(512),
  label: z.string().max(512)
}).strict()

/** Zod schema for the playback-heartbeat JSON body. */
export const playbackHeartbeatBodySchema = z.object({
  storageUsedPercent: z.number().min(0).max(100).optional(),
  assetPlays: z.array(assetPlaySchema).max(PLAYBACK_HEARTBEAT_MAX_ASSET_PLAYS).optional(),
  orderId: z.string().uuid().optional(),
  networkOperatorId: z.string().uuid().optional(),
  playlistUrn: z.string().optional(),
  /** Same as `playlistUrn`; if both are set they must match. */
  playlistId: z.string().optional(),
  playerGeohash: playbackHeartbeatPlayerGeohashSchema.optional(),
  hardwareCapacity: hardwareCapacitySnapshotSchema.optional(),
  version: z.string().optional(),
  webVersion: z.string().optional(),
  screenRotation: screenRotationSchema.optional(),
  screenOrientation: screenOrientationSchema.optional(),
  currentStatusOverlayVisible: z.boolean().optional(),
  timersOverlayVisible: z.boolean().optional(),
  /** Android hardware id prefix (max 8) for operator correlation with on-device Current State. */
  androidIdPrefix: z.string().min(1).max(8).optional(),
  /** Android Device Owner (shell readiness / silent APK OTA). */
  deviceOwner: z.boolean().optional(),
  /** Lucimark Accessibility enabled (remote view/control). */
  accessibilityEnabled: z.boolean().optional(),
  ...playerPowerHeartbeatFieldsSchema,
  ...playerBrightnessHeartbeatFieldsSchema,
  audienceAggregate: audienceHeartbeatAggregateSchema.optional(),
  audienceVideoInputDevices: z.array(audienceVideoInputDeviceEntrySchema).max(32).optional(),
  utcOffsetMinutes: z.number().int().min(-720).max(840).optional(),
  /** Upper bound: 7 days of loop length (sanity cap for pathological schedules). */
  segmentResolvedLoopSeconds: z.number().finite().positive().max(604_800).optional(),
  /** Viewport width (CSS px); send together with `screenHeightPx` or omit both. */
  screenWidthPx: z.number().int().min(1).max(32_768).optional(),
  /** Viewport height (CSS px); send together with `screenWidthPx` or omit both. */
  screenHeightPx: z.number().int().min(1).max(32_768).optional(),
  /** See {@link IPlaybackHeartbeatBody.weeklyAvailabilityPlaybackSuppressed}. */
  weeklyAvailabilityPlaybackSuppressed: z.boolean().optional(),
  playbackTimerStuckSuspected: z.boolean().optional(),
  playbackTimerStuckOverrunMs: z.number().int().min(0).max(86_400_000).optional(),
  playbackTimerStuckFrameId: z.string().min(1).max(512).optional(),
  playbackTimerStuckVisibleDwellMs: z.number().int().min(0).max(86_400_000).optional(),
  playbackTimerStuckTimingFlatlineMs: z.number().int().min(0).max(86_400_000).optional(),
  playerDiagnostics: playerDiagnosticsSnapshotSchema.optional(),
  lastBootAt: z.string().min(1).max(64).optional(),
  lastBootWasCleanShutdown: z.boolean().optional(),
  lastBootHadBatteryOrPowerEvent: z.boolean().optional(),
  lastBootKind: z.enum(['cold_boot', 'app_restart']).optional()
}).superRefine((data, ctx) => {
  if (
    data.playlistUrn != null && data.playlistUrn !== ''
    && data.playlistId != null && data.playlistId !== ''
    && data.playlistUrn !== data.playlistId
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'playlistUrn and playlistId must match when both are set',
      path: ['playlistId']
    })
  }
  const hasW = data.screenWidthPx != null
  const hasH = data.screenHeightPx != null
  if (hasW !== hasH) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'screenWidthPx and screenHeightPx must both be set or both omitted',
      path: hasW ? ['screenHeightPx'] : ['screenWidthPx']
    })
  }
  if (data.playbackTimerStuckSuspected === true) {
    if (data.playbackTimerStuckOverrunMs == null || data.playbackTimerStuckOverrunMs <= 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'playbackTimerStuckOverrunMs must be a positive integer when playbackTimerStuckSuspected is true',
        path: ['playbackTimerStuckOverrunMs']
      })
    }
  }
  if (data.playbackTimerStuckSuspected !== true) {
    if (data.playbackTimerStuckOverrunMs != null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'playbackTimerStuckOverrunMs is only allowed when playbackTimerStuckSuspected is true',
        path: ['playbackTimerStuckOverrunMs']
      })
    }
    if (data.playbackTimerStuckFrameId != null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'playbackTimerStuckFrameId is only allowed when playbackTimerStuckSuspected is true',
        path: ['playbackTimerStuckFrameId']
      })
    }
    if (data.playbackTimerStuckVisibleDwellMs != null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'playbackTimerStuckVisibleDwellMs is only allowed when playbackTimerStuckSuspected is true',
        path: ['playbackTimerStuckVisibleDwellMs']
      })
    }
    if (data.playbackTimerStuckTimingFlatlineMs != null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'playbackTimerStuckTimingFlatlineMs is only allowed when playbackTimerStuckSuspected is true',
        path: ['playbackTimerStuckTimingFlatlineMs']
      })
    }
  }
})

export type TPlaybackHeartbeatBody = z.infer<typeof playbackHeartbeatBodySchema>
